/* <mel-quick-add> + the quick-view dialog for the PDP deal box (client v15c).
 *
 * Why not Horizon's quick-add dialog any more: it fetched the WHOLE product
 * page (~400 KB), morphed Shopify's product UI into a modal, showed one 74px
 * thumbnail on phones and its own price block (compare-at not struck) — the
 * client saw "1 image", "slow", "not crossed out". This dialog is built from
 * what the tile already knows (image, name, the variant <select> with prices)
 * and one small /products/<handle>.js (images, variants, compare-at,
 * availability; ~5 KB, prefetched on hover/touch, cached per product):
 *   1. tap → the dialog opens NOW with the tile's image, title, price and a
 *      sizes skeleton;
 *   2. the JSON lands (~100 ms) → every image (swipe strip + count + thumb
 *      row on phones, thumb rail + 4:5 plate on desktop), option pills with
 *      unavailable ones disabled, struck compare-at;
 *   3. ADD TO CART · price — the tile's own product-form-component, cloned, so
 *      the add goes through the standard cart events; it is quiet
 *      (data-mel-quiet: no drawer auto-open) and tagged deal_popup; on success
 *      the dialog closes and <mel-toast> (melaninful.js) confirms.
 * Nothing in the dialog links off the page. Demo the client confirmed:
 * plans/reports/advise-260917-2304-pdp-popup-demo.html
 */
(function () {
  'use strict';
  /* product data, normalised: { handle, images:[url], options:[name], variants:[{id, options:[..], available|null, price¢, compare¢}] }.
     /products/<handle>.js carries availability; /products/<handle>.json (the fallback — the theme-dev
     proxy 502s the .js path) does not, so `available` is null there and the caller derives it from the
     tile's select (which lists available variants only). */
  var products = new Map(); /* handle → Promise<product> */
  function normalizeJs(p) {
    /* .js options are strings on old shops, {name,…} objects on new ones */
    return { handle: p.handle, images: p.images || [], options: (p.options || []).map(function (o) { return typeof o === 'string' ? o : o.name; }), variants: (p.variants || []).map(function (v) {
      return { id: String(v.id), options: [v.option1, v.option2, v.option3].filter(function (x) { return x != null; }), available: !!v.available, price: v.price, compare: v.compare_at_price };
    }) };
  }
  function normalizeJson(p) {
    var cents = function (s) { var n = parseFloat(s); return isNaN(n) ? null : Math.round(n * 100); };
    return { handle: p.handle, images: (p.images || []).map(function (i) { return i.src; }), options: (p.options || []).map(function (o) { return o.name; }), variants: (p.variants || []).map(function (v) {
      return { id: String(v.id), options: [v.option1, v.option2, v.option3].filter(function (x) { return x != null; }), available: null, price: cents(v.price), compare: cents(v.compare_at_price) };
    }) };
  }
  function fetchProduct(handle) {
    if (!products.has(handle)) {
      var get = function (url) { return fetch(url, { headers: { Accept: 'application/json' } }).then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); }); };
      products.set(handle, get('/products/' + handle + '.js').then(normalizeJs)
        .catch(function () { return get('/products/' + handle + '.json').then(function (j) { return normalizeJson(j.product || j); }); })
        .catch(function (err) { products.delete(handle); throw err; }));
    }
    return products.get(handle);
  }
  function sized(url, w) {
    if (!url) return '';
    url = url.replace(/^\/\//, 'https://');
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }
  /* money: mirror the store's format from a rendered sample ("$64.99") */
  function moneyFormatter(sample) {
    var m = /([^\d]*)(\d[\d.,]*)(.*)/.exec(sample || '$0.00') || [null, '$', '0.00', ''];
    var num = m[2], dec = /[.,](\d{1,2})$/.exec(num), decimals = dec ? dec[1].length : 0, sep = dec ? num.charAt(num.length - dec[1].length - 1) : '.';
    var thou = decimals ? num.slice(0, -dec[1].length - 1) : num, thouSep = /[.,\s]/.exec(thou.replace(/^\d+/, '')) ? thou.replace(/^\d+/, '').charAt(0) : (sep === ',' ? '.' : ',');
    /* a < 1,000 sample cannot show its thousands separator: assume the one Shopify pairs with the decimal mark */
    return function (cents) {
      var whole = Math.floor(cents / 100), frac = String(cents % 100).padStart(2, '0').slice(0, decimals);
      var w = String(whole);
      if (thouSep) w = w.replace(/\B(?=(\d{3})+(?!\d))/g, thouSep);
      return m[1] + w + (decimals ? sep + frac : '') + m[3];
    };
  }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  var QV = {
    el: null, trigger: null, model: null, sel: null,
    ensure: function () {
      if (this.el) return this.el;
      var d = document.createElement('dialog');
      d.className = 'mel-qv';
      d.setAttribute('aria-label', 'Quick view');
      d.innerHTML =
        '<button type="button" class="mel-qv__close" data-qv-close aria-label="Close">&times;</button>' +
        /* client v16: title, price, option pills and ADD TO CART sit together in
           the pinned block (phones: bottom, capped at 55dvh, scrolling inside past
           that; desktop: right column). The gallery takes whatever is left —
           nothing else scrolls, the thumb row is always in view (it replaced the
           dots). */
        '<div class="mel-qv__gal">' +
          '<div class="mel-qv__thumbs" data-qv-thumbs></div>' +
          '<div class="mel-qv__plate"><div class="mel-qv__track" data-qv-track></div><span class="mel-qv__count" data-qv-count></span></div>' +
        '</div>' +
        '<div class="mel-qv__pin">' +
          '<div class="mel-qv__head">' +
            '<h2 class="mel-qv__title" data-qv-title></h2>' +
            '<div class="mel-qv__price" data-qv-price></div>' +
          '</div>' +
          '<div class="mel-qv__opts" data-qv-opts><div class="mel-qv__skel"><i></i><i></i><i></i><i></i></div></div>' +
          '<div class="mel-qv__form" data-qv-form></div>' +
        '</div>';
      var self = this;
      d.addEventListener('click', function (e) {
        var t;
        if (e.target === d || e.target.closest('[data-qv-close]')) { self.close(); return; }
        if ((t = e.target.closest('[data-qv-img]'))) { self.show(Number(t.dataset.qvImg), true); return; }
        if ((t = e.target.closest('[data-qv-opt]')) && !t.disabled) { self.pick(Number(t.dataset.qvAxis), t.dataset.qvOpt); }
      });
      d.addEventListener('close', function () { document.documentElement.classList.remove('mel-lock'); if (self.trigger && self.trigger.isConnected) self.trigger.focus({ preventScroll: true }); });
      d.querySelector('[data-qv-track]').addEventListener('scroll', function (e) {
        var tr = e.target, i = Math.round(tr.scrollLeft / Math.max(1, tr.clientWidth));
        if (i !== self.img) self.show(i, false);
      }, { passive: true });
      /* a successful add from inside the dialog closes it; the toast + tally
         repaint come from melaninful.js's quiet-add listener */
      document.addEventListener('shopify:cart:lines-update', function (e) {
        if (!e.promise || !(e.target instanceof Element) || !d.contains(e.target)) return;
        e.promise.then(function (r) { if (r && r.detail && !r.detail.didError) self.close(); }).catch(function () {});
      });
      document.body.appendChild(d);
      this.el = d;
      return d;
    },
    open: function (trigger) {
      var d = this.ensure(), tile = trigger.closest('.dtile'), self = this;
      var select = tile && tile.querySelector('select.qrow-size');
      var form = tile && tile.querySelector('product-form-component');
      if (!tile || !form) return;
      this.trigger = trigger.querySelector('button') || trigger;
      var opts = select ? Array.prototype.map.call(select.options, function (o) {
        return { id: o.value, price: o.dataset.price, style: o.dataset.style, size: o.dataset.size };
      }) : [];
      var current = select ? select.value : (form.querySelector('[name="id"]') || {}).value;
      var cur = opts.filter(function (o) { return o.id === current; })[0] || opts[0];
      var fmt = moneyFormatter(cur && cur.price);
      /* shell: everything the tile knows, before any network */
      var model = this.model = { handle: trigger.dataset.handle, title: trigger.dataset.title, fmt: fmt, images: [trigger.dataset.image], thumbs: [trigger.dataset.image], options: null, variants: null, sel: null, current: current, priceText: cur ? cur.price : '' };
      d.querySelector('[data-qv-title]').textContent = this.model.title;
      d.querySelector('[data-qv-price]').innerHTML = '<b>' + esc(this.model.priceText) + '</b>';
      d.querySelector('[data-qv-opts]').innerHTML = '<div class="mel-qv__skel" aria-hidden="true"><i></i><i></i><i></i><i></i></div>';
      this.renderGallery();
      /* the tile's own form, cloned: hidden variant id instead of the select */
      var fc = form.cloneNode(true);
      fc.removeAttribute('data-mel-init');
      fc.removeAttribute('data-adding'); /* review v15: a clone taken mid-add kept the spinner state forever */
      fc.dataset.melList = 'deal_popup';
      fc.classList.remove('dtile-form');
      fc.classList.add('mel-qv__pform');
      var sel = fc.querySelector('select[name="id"]');
      if (sel) {
        var hid = document.createElement('input');
        hid.type = 'hidden'; hid.name = 'id'; hid.setAttribute('ref', 'variantId'); hid.value = current;
        sel.replaceWith(hid);
      }
      var btn = fc.querySelector('.qrow-add');
      if (btn) { btn.className = 'mel-qv__add'; btn.innerHTML = esc(trigger.dataset.addLabel || 'Add to cart') + ' &middot; <i data-qv-btn-price>' + esc(this.model.priceText) + '</i>'; }
      var host = d.querySelector('[data-qv-form]');
      host.textContent = '';
      host.appendChild(fc);
      document.documentElement.classList.add('mel-lock');
      if (!d.open) d.showModal();
      d.querySelector('.mel-qv__pin').scrollTop = 0;
      /* enrich: every image, all variants, compare-at, availability */
      fetchProduct(model.handle).then(function (p) {
        if (self.model !== model) return; /* another product opened meanwhile */
        var known = {}; opts.forEach(function (o) { known[o.id] = true; });
        var raw = p.images && p.images.length ? p.images : [trigger.dataset.image];
        self.model.images = raw.map(function (u) { return sized(u, 900); });
        self.model.thumbs = raw.map(function (u) { return sized(u, 200); });
        self.model.options = p.options;
        self.model.variants = p.variants.map(function (v) {
          return { id: v.id, options: v.options, available: v.available === null ? (opts.length ? !!known[v.id] : true) : v.available, price: fmt(v.price), compare: v.compare && v.compare > v.price ? fmt(v.compare) : '' };
        });
        var curV = self.model.variants.filter(function (v) { return v.id === self.model.current; })[0] || self.model.variants.filter(function (v) { return v.available; })[0] || self.model.variants[0];
        self.model.sel = curV ? curV.options.slice() : [];
        self.renderGallery();
        self.renderOptions();
        self.applyVariant();
      }).catch(function () {
        if (self.model !== model) return; /* review v15: the fallback must not paint A's sizes/id into B's dialog */
        /* offline / 404: sizes from the tile's select (available variants only) */
        var styles = [], sizes = [];
        opts.forEach(function (o) { if (o.style && styles.indexOf(o.style) < 0) styles.push(o.style); if (o.size && sizes.indexOf(o.size) < 0) sizes.push(o.size); });
        self.model.options = [trigger.dataset.optStyle || 'Style', trigger.dataset.optSize || 'Size'].filter(function (n, i) { return i === 0 ? styles.length > 0 : true; });
        self.model.variants = opts.map(function (o) { return { id: o.id, options: styles.length ? [o.style, o.size] : [o.size], available: true, price: o.price, compare: '' }; });
        self.model.sel = cur ? (styles.length ? [cur.style, cur.size] : [cur.size]) : [];
        self.renderOptions();
        self.applyVariant();
      });
    },
    renderGallery: function () {
      var d = this.el, m = this.model, n = m.images.length;
      d.querySelector('[data-qv-track]').innerHTML = m.images.map(function (u, i) { return '<img src="' + esc(u) + '" alt=""' + (i > 1 ? ' loading="lazy"' : '') + '>'; }).join('');
      d.querySelector('[data-qv-thumbs]').innerHTML = n > 1 ? m.thumbs.map(function (u, i) { return '<button type="button" data-qv-img="' + i + '" aria-pressed="' + (i === 0) + '" aria-label="Image ' + (i + 1) + '"><img src="' + esc(u) + '" alt="" loading="lazy"></button>'; }).join('') : '';
      d.querySelector('[data-qv-count]').textContent = n > 1 ? '1 / ' + n : '';
      d.querySelector('[data-qv-count]').hidden = n < 2;
      this.img = 0;
      d.querySelector('[data-qv-track]').scrollLeft = 0;
    },
    show: function (i, scroll) {
      var d = this.el, tr = d.querySelector('[data-qv-track]'), strip = d.querySelector('[data-qv-thumbs]');
      this.img = i;
      if (scroll) tr.scrollTo({ left: tr.clientWidth * i, behavior: 'smooth' });
      d.querySelectorAll('[data-qv-thumbs] button').forEach(function (b, k) { b.setAttribute('aria-pressed', k === i ? 'true' : 'false'); });
      d.querySelector('[data-qv-count]').textContent = (i + 1) + ' / ' + this.model.images.length;
      /* keep the active thumb in view — the strip is the only position cue besides the count */
      var on = strip.children[i];
      if (on) {
        var a = on.getBoundingClientRect(), s = strip.getBoundingClientRect(), dx = 0, dy = 0;
        if (a.left < s.left) dx = a.left - s.left - 12; else if (a.right > s.right) dx = a.right - s.right + 12;
        if (a.top < s.top) dy = a.top - s.top - 6; else if (a.bottom > s.bottom) dy = a.bottom - s.bottom + 6;
        if (dx || dy) strip.scrollBy({ left: dx, top: dy, behavior: 'smooth' });
      }
    },
    /* an option value is available if some variant with the OTHER current picks carries it */
    availableFor: function (axis, value) {
      var m = this.model;
      return m.variants.some(function (v) {
        if (!v.available || v.options[axis] !== value) return false;
        return m.sel.every(function (s, k) { return k === axis || v.options[k] === s; });
      });
    },
    renderOptions: function () {
      var d = this.el, m = this.model, self = this, html = '';
      if (m.variants.length === 1 && m.options.length <= 1) { d.querySelector('[data-qv-opts]').innerHTML = ''; return; }
      m.options.forEach(function (name, axis) {
        var values = [];
        m.variants.forEach(function (v) { if (values.indexOf(v.options[axis]) < 0) values.push(v.options[axis]); });
        html += '<div class="mel-qv__opt"><div class="mel-qv__lab">' + esc(name) + '</div><div class="mel-qv__pills" role="group" aria-label="' + esc(name) + '">' +
          values.map(function (val) {
            var on = m.sel[axis] === val, ok = self.availableFor(axis, val);
            return '<button type="button" class="mel-qv__pill" data-qv-axis="' + axis + '" data-qv-opt="' + esc(val) + '" aria-pressed="' + on + '"' + (ok ? '' : ' disabled') + '>' + esc(val) + '</button>';
          }).join('') + '</div></div>';
      });
      d.querySelector('[data-qv-opts]').innerHTML = html;
    },
    pick: function (axis, value) {
      var m = this.model;
      m.sel[axis] = value;
      /* keep the other axes on an available combination */
      var match = m.variants.filter(function (v) { return v.available && v.options.every(function (o, k) { return o === m.sel[k]; }); })[0];
      if (!match) {
        var alt = m.variants.filter(function (v) { return v.available && v.options[axis] === value; })[0];
        if (alt) m.sel = alt.options.slice();
      }
      this.renderOptions();
      this.applyVariant();
    },
    applyVariant: function () {
      var d = this.el, m = this.model;
      var v = m.variants.filter(function (x) { return x.options.every(function (o, k) { return o === m.sel[k]; }); })[0];
      if (!v) return;
      m.current = v.id;
      d.querySelector('[data-qv-price]').innerHTML = '<b' + (v.compare ? ' class="sale"' : '') + '>' + esc(v.price) + '</b>' + (v.compare ? '<s>' + esc(v.compare) + '</s>' : '');
      var hid = d.querySelector('[data-qv-form] input[name="id"]'); if (hid) hid.value = v.id;
      var bp = d.querySelector('[data-qv-btn-price]'); if (bp) bp.textContent = v.price;
      var add = d.querySelector('.mel-qv__add'); if (add) add.disabled = !v.available;
    },
    close: function () { if (this.el && this.el.open) this.el.close(); }
  };

  if (!window.customElements.get('mel-quick-add')) {
    window.customElements.define('mel-quick-add', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.melInit) return;
        this.dataset.melInit = '1';
        var self = this, warm = function () { if (self.dataset.handle) fetchProduct(self.dataset.handle).catch(function () {}); };
        this.addEventListener('click', function (e) { if (e.target.closest('button')) { e.preventDefault(); QV.open(self); } });
        this.addEventListener('pointerenter', warm);
        this.addEventListener('touchstart', warm, { passive: true });
        this.addEventListener('focusin', warm);
      }
    });
  }
})();
